import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { ArrowRight, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import Seo, { SITE_URL, SITE_NAME } from "../components/Seo";
import LaserEtchedPrice from "../components/LaserEtchedPrice";
import LaserEtchedLabel from "../components/LaserEtchedLabel";
import { variantAvailability, variantCanBePurchased } from "../lib/variantAvailability";

const INITIAL_VISIBLE = 18;
const isBrokenGalleryItem = (item = {}) => {
  const title = String(item.title || "").toLowerCase();
  const image = String(item.image_url || item.image || "").toLowerCase();

  return title.includes("test studio sign")
    || title.includes("gallery reorder")
    || title.includes("sample")
    || image.includes("assets/none")
    || image.includes("example.com")
    || image.includes("none");
};

const sanitizeGalleryItems = (items = []) => items.filter((item) => !isBrokenGalleryItem(item));
const dollars = (minor) => `$${(Number(minor || 0) / 100).toFixed(2)}`;

export default function Gallery() {
  const location = useLocation();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const initial = location.state?.category || "All";
  const [active, setActive] = useState(initial);
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const [selectedItem, setSelectedItem] = useState(null);
  const [dbItems, setDbItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [selectedVariants, setSelectedVariants] = useState({});
  const [activePriceId, setActivePriceId] = useState(null);
  const [customizationDraft, setCustomizationDraft] = useState({ options: {}, personalization: {}, additional_notes: "" });

  useEffect(() => {
    const loadItems = async () => {
      try {
        const galleryResponse = await api.get("/gallery");
        setDbItems(Array.isArray(galleryResponse.data) ? galleryResponse.data : []);
        setLoadError("");
      } catch (error) {
        setDbItems([]);
        setLoadError(formatApiError(error));
      } finally {
        setLoading(false);
      }
    };
    loadItems();
  }, []);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
  }, [active]);

  const catalogItems = useMemo(() => sanitizeGalleryItems(dbItems).map((item) => {
    const itemCategories = Array.isArray(item.categories) ? item.categories : [item.category].filter(Boolean);
    return {
      ...item,
      id: item.id || item._id || `${item.title}-${item.image_url || item.image}`,
      image: item.image || item.image_url,
      categories: itemCategories,
      product: item.product,
    };
  }), [dbItems]);
  const galleryCategories = ["All", ...new Set(catalogItems.flatMap((item) => item.categories || []))];

  useEffect(() => {
    const itemId = new URLSearchParams(location.search).get("item");
    if (!itemId || loading) return;
    const item = catalogItems.find((entry) => entry.id === itemId);
    if (item) {
      setSelectedItem(item);
      setCustomizationDraft({ options: {}, personalization: {}, additional_notes: "" });
    }
  }, [catalogItems, loading, location.search]);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return catalogItems.filter((item) => {
      const matchesCategory = active === "All" || (item.categories || []).includes(active);
      if (!matchesCategory) return false;
      if (!query) return true;
      const haystack = [item.title, item.description, ...(item.categories || [])].join(" ").toLowerCase();
      return haystack.includes(query);
    });
  }, [active, search, catalogItems]);

  const visibleItems = filteredItems.slice(0, visibleCount);
  const hasMore = visibleCount < filteredItems.length;

  const absoluteImage = (url) => {
    if (!url) return `${SITE_URL}/branding/bbw-logo.jpg`;
    return /^https?:\/\//i.test(url) ? url : `${SITE_URL}${url.startsWith("/") ? url : `/${url}`}`;
  };

  const productJsonLd = useMemo(() => {
    const products = catalogItems
      .filter((item) => item.product && !item.product.needs_pricing && Number.isFinite(item.product.price_minor) && item.product.price_minor > 0)
      .map((item) => {
        const product = item.product;
        const tracked = (product.variants || []).filter((variant) => typeof variant.available === "number");
        const inStock = tracked.length === 0 || tracked.some((variant) => variant.available > 0);
        return {
          "@type": "Product",
          name: item.title,
          image: absoluteImage(item.image),
          description: item.description || `${item.title} — a custom laser-cut basswood piece by ${SITE_NAME}.`,
          brand: { "@type": "Brand", name: SITE_NAME },
          url: `${SITE_URL}/gallery`,
          offers: {
            "@type": "Offer",
            url: `${SITE_URL}/gallery`,
            priceCurrency: product.currency || "USD",
            price: (Number(product.price_minor) / 100).toFixed(2),
            availability: inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          },
        };
      });
    if (!products.length) return null;
    return { "@context": "https://schema.org", "@graph": products };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbItems]);

  const openItem = (item) => {
    setSelectedItem(item);
    setCustomizationDraft({ options: {}, personalization: {}, additional_notes: "" });
  };
  const closeItem = () => {
    setSelectedItem(null);
    const params = new URLSearchParams(location.search);
    if (!params.has("item")) return;
    params.delete("item");
    navigate({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : "",
      hash: location.hash,
    }, { replace: true, state: location.state });
  };
  const addCustomizedItem = () => {
    if (!selectedItem?.product) return;
    addItem({ product_id: selectedItem.product.id, variant_id: selectedVariants[selectedItem.product.id] || null, selected_options: customizationDraft.options, personalization: customizationDraft.personalization, additional_notes: customizationDraft.additional_notes || null });
    closeItem();
  };
  const customizationEstimate = selectedItem?.product?.customization?.enabled ? (() => {
    const product = selectedItem.product;
    const variant = product.variants?.find((item) => item.id === selectedVariants[product.id]);
    const base = variant?.sale_price_minor ?? variant?.price_minor ?? product.sale_price_minor ?? product.price_minor ?? 0;
    const optionAdjustment = (product.customization.option_groups || []).flatMap((group) => group.options || []).filter((option) => customizationDraft.options[group.key] === option.key).reduce((sum, option) => sum + Number(option.price_adjustment_minor || 0), 0);
    const textAdjustment = (product.customization.text_fields || []).filter((field) => customizationDraft.personalization[field.key]?.trim()).reduce((sum, field) => sum + Number(field.price_adjustment_minor || 0), 0);
    return base + optionAdjustment + textAdjustment;
  })() : null;

  return (
    <div className="gallery-workshop min-h-screen" data-testid="gallery-page">
      <Seo
        title="Personalized Wood Signs & Custom Decor"
        description="Browse personalized wood signs, custom door decor, seasonal designs, pet signs, sports pieces, patriotic decor, and more from Beyond Board Woodworking."
        path="/gallery"
        jsonLd={productJsonLd}
      />
      <section className="gallery-intro max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-12">
        <p className="text-xs uppercase tracking-[0.28em] text-[#26C6D5] mb-3">Our Work</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F5F0E6] leading-tight">Custom Wood Signs & Personalized Decor</h1>
        <p className="mt-6 text-[#B8B7B2] max-w-2xl text-lg leading-relaxed">
          Browse real designs from Beyond Board Woodworking, including personalized door signs, seasonal decor, pets and animals, sports, patriotic pieces, outdoor themes, home signs, and novelty designs.
        </p>
        <p className="mt-3 text-[#B8B7B2] max-w-2xl text-lg leading-relaxed">
          See something close to what you want? Use it for inspiration or start a custom order for something more personal.
        </p>
      </section>

      <section className="gallery-filter-band sticky top-16 z-30">
        <div className="gallery-filter-bar max-w-7xl mx-auto px-6 lg:px-10 py-4">
          <div className="mb-3">
            <input
              type="search"
              value={search}
              onChange={(event) => { setSearch(event.target.value); setVisibleCount(INITIAL_VISIBLE); }}
              placeholder="Search pieces..."
              aria-label="Search pieces"
              data-testid="gallery-search"
              className="w-full sm:max-w-sm rounded-md border border-stone-100/15 bg-zinc-900 px-4 py-2 text-sm text-stone-100 placeholder:text-stone-500 focus:outline-none focus:ring-1 focus:ring-[#26C6D5]"
            />
          </div>
          <div className="gallery-filter-scroll flex gap-2 overflow-x-auto pb-1">
            {galleryCategories.map((cat) => (
              <button
                key={cat}
                data-testid={`gallery-filter-${cat.replace(/\s/g, "-").toLowerCase()}`}
                onClick={() => setActive(cat)}
                className={`gallery-filter-tag whitespace-nowrap px-4 py-2 rounded-md text-sm transition border ${
                  active === cat
                    ? "gallery-filter-active"
                    : ""
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="mb-6 flex items-center justify-between gap-4 text-sm text-[#B8B7B2]">
          <span>{loading ? "Loading gallery..." : `${filteredItems.length} pieces`}</span>
          {active !== "All" && <span>Filtered by: <span className="text-[#26C6D5]">{active}</span></span>}
          {search.trim() && <span>Search: <span className="text-[#26C6D5]">{search.trim()}</span></span>}
        </div>

        {loadError && <div className="mb-6 rounded-xl border border-rose-400/30 bg-rose-950/20 p-4 text-rose-200">Unable to load the gallery from the server. <button type="button" onClick={() => window.location.reload()} className="underline">Retry</button></div>}

        {!loading && filteredItems.length === 0 ? (
          <p className="text-stone-400 py-8">{search.trim() ? `No pieces match "${search.trim()}".` : "No pieces in this category yet — check back soon."}</p>
        ) : null}

        {!loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="gallery-grid">
            {visibleItems.map((item, i) => (
              <article
                key={item.id}
                data-testid={`gallery-card-${item.id}`}
                tabIndex={0}
                data-laser-plaque
                className="gallery-product-card group rounded-lg overflow-hidden fade-up cursor-pointer"
                style={{ animationDelay: `${i * 30}ms` }}
                onPointerEnter={() => setActivePriceId(item.id)}
                onPointerLeave={() => setActivePriceId(null)}
                onFocus={() => setActivePriceId(item.id)}
                onBlur={() => setActivePriceId(null)}
                onClick={() => openItem(item)}
              >
                <div className="gallery-image-bay relative aspect-[4/3] overflow-hidden">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover transition duration-500" loading="lazy" />
                  {activePriceId === item.id && item.product?.sale_price_minor != null && item.product?.regular_price_minor != null && item.product.sale_price_minor < item.product.regular_price_minor && (
                    <LaserEtchedPrice replayKey={item.id} className="customer-wood-price absolute bottom-3 left-3" segments={[{ text: dollars(item.product.regular_price_minor), className: "mr-2 text-sm text-stone-600 line-through", animate: false }, { text: dollars(item.product.sale_price_minor), className: "text-lg font-semibold text-[#3a1d0f]" }]} />
                  )}
                  {activePriceId === item.id && item.product && !item.product.needs_pricing && !(item.product.sale_price_minor != null && item.product.regular_price_minor != null && item.product.sale_price_minor < item.product.regular_price_minor) && item.product.product_type !== "mason_jar" && (
                    <LaserEtchedPrice replayKey={item.id} className="customer-wood-price absolute bottom-3 left-3" segments={[{ text: dollars(item.product.price_minor), className: "text-lg font-semibold text-[#3a1d0f]" }]} />
                  )}
                </div>
                <div className="gallery-card-copy p-5">
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(item.categories || []).slice(0, 2).map((cat) => (
                      <LaserEtchedLabel key={`${item.id}-${cat}`} text={cat} kind="plaque" delay={i * 80} onceKey={`gallery-category-${item.id}`} className="gallery-category-label text-[10px] uppercase tracking-[0.18em]" />
                    ))}
                  </div>
                  <LaserEtchedLabel as="h3" text={item.title} kind="plaque" delay={i * 80} onceKey={`gallery-title-${item.id}`} className="gallery-card-title font-display text-2xl" />
                  <p className="gallery-card-description text-sm mt-2 line-clamp-3">{item.description}</p>
                  {item.product?.customization?.enabled && <p className="gallery-customization-badge mt-3 text-xs uppercase tracking-[0.18em]">{item.product.customization.badge_text || "CUSTOMIZABLE"}</p>}
                  {item.product?.needs_pricing ? <p className="mt-4 text-sm text-[#D8A34B]">Available soon — pricing is being finalized.</p> : item.product && <div className="mt-4" onClick={(event) => event.stopPropagation()}>{item.product.product_type === "mason_jar" && <select value={selectedVariants[item.product.id] || ""} onChange={(event) => setSelectedVariants((current) => ({ ...current, [item.product.id]: event.target.value }))} className="gallery-variant-select mb-2 w-full rounded-md px-2 py-2 text-sm"><option value="">Choose size</option>{item.product.variants.filter((variant) => variant.active !== false).map((variant) => { const availability = variantAvailability(variant); return <option key={variant.id} value={variant.id} disabled={!availability.purchasable}>{variant.name} - {dollars(variant.sale_price_minor ?? variant.price_minor)} · {availability.label}</option>; })}</select>}<Button type="button" disabled={item.product.product_type === "mason_jar" && (!selectedVariants[item.product.id] || !variantCanBePurchased(item.product.variants.find((variant) => variant.id === selectedVariants[item.product.id])))} onClick={() => item.product.customization?.enabled ? openItem(item) : addItem({ product_id: item.product.id, variant_id: selectedVariants[item.product.id] || null })} className="gallery-card-action w-full">{item.product.customization?.enabled ? "Customize & Add" : "Add to Cart"}</Button></div>}
                </div>
              </article>
            ))}
          </div>
        ) : null}

        {hasMore && !loading && (
          <div className="mt-10 text-center">
            <Button
              onClick={() => setVisibleCount((count) => Math.min(count + 12, filteredItems.length))}
              className="bg-[#26C6D5] hover:bg-[#1ab7d2] text-[#15191C] rounded-md px-6"
            >
              Load More
            </Button>
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <div className="rounded-2xl border border-[#D8A34B]/30 bg-[#D8A34B]/5 p-6 text-center">
          <h2 className="font-display text-2xl text-[#F5F0E6]">Can't Find Exactly What You Want?</h2>
          <p className="mt-2 text-[#B8B7B2] max-w-xl mx-auto">
            Use one of our designs for inspiration or tell us what you have in mind and start a custom order.
          </p>
          <Link to="/quote">
            <Button className="mt-4 bg-[#D8A34B] hover:bg-[#c6903b] text-[#15191C] rounded-md px-6">
              Start a Custom Order
            </Button>
          </Link>
        </div>
      </section>

      {selectedItem && (
        <div data-testid="gallery-item-dialog" className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeItem}>
          <div className="relative w-full max-w-5xl rounded-3xl overflow-hidden border border-stone-100/10 bg-zinc-950 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={closeItem}
              className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-zinc-900/80 text-stone-100 border border-stone-100/10"
              aria-label="Close preview"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="grid md:grid-cols-[1.35fr_0.65fr]">
              <div className="bg-zinc-950">
                <img src={selectedItem.image} alt={selectedItem.title} className="w-full h-full object-contain max-h-[72vh] bg-zinc-950" />
              </div>
              <div className="p-6 sm:p-8 bg-zinc-950/95">
                {Array.isArray(selectedItem.categories) && selectedItem.categories.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {selectedItem.categories.map((cat) => (
                      <span key={`${selectedItem.id}-${cat}`} className="rounded-full border border-[#D8A34B]/30 bg-[#D8A34B]/10 px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-[#D8A34B]">
                        {cat}
                      </span>
                    ))}
                  </div>
                )}
                <h2 className="font-display text-4xl text-[#F5F0E6] leading-none">{selectedItem.title}</h2>
                <p className="mt-4 text-[#B8B7B2] leading-relaxed">{selectedItem.description}</p>
                {selectedItem.product?.needs_pricing ? (
                  <p className="mt-6 text-sm text-[#D8A34B]">Pricing is being finalized. Request a custom version for details.</p>
                ) : selectedItem.product?.product_type === "mason_jar" ? (
                  <div className="mt-6 rounded-xl border border-stone-100/10 bg-zinc-900/70 p-4">
                    <p className="text-xs uppercase tracking-[0.2em] text-stone-500">Available sizes</p>
                    <div className="mt-3 space-y-2">
                      {(selectedItem.product.variants || []).filter((variant) => variant.active !== false).map((variant) => (
                        <div key={variant.id} className="flex items-center justify-between gap-4 text-stone-200">
                          <span>{variant.name}<span className="ml-2 text-xs text-stone-400">{variantAvailability(variant).label}</span></span>
                          <span className="font-semibold text-[#D8A34B]">{dollars(variant.sale_price_minor ?? variant.price_minor)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : selectedItem.product && (
                  <div className="mt-6 flex items-baseline gap-3">
                    {selectedItem.product.sale_price_minor != null && selectedItem.product.regular_price_minor != null && selectedItem.product.sale_price_minor < selectedItem.product.regular_price_minor ? (
                      <>
                        <span className="text-lg text-stone-500 line-through">{dollars(selectedItem.product.regular_price_minor)}</span>
                        <span className="text-3xl font-semibold text-[#D8A34B]">{dollars(selectedItem.product.sale_price_minor)}</span>
                      </>
                    ) : (
                      <span className="text-3xl font-semibold text-[#D8A34B]">{dollars(selectedItem.product.price_minor)}</span>
                    )}
                  </div>
                )}
                {selectedItem.product?.customization?.enabled && <div className="mt-6 space-y-4 rounded-xl border border-[#26C6D5]/25 bg-[#26C6D5]/5 p-4"><p className="text-sm text-[#8FE8F8]">{selectedItem.product.customization.customer_message}</p>{(selectedItem.product.customization.option_groups || []).map((group) => <label key={group.key} className="block text-sm text-stone-200">{group.label}{group.required ? " *" : ""}<select value={customizationDraft.options[group.key] || ""} onChange={(event) => setCustomizationDraft((current) => ({ ...current, options: { ...current.options, [group.key]: event.target.value } }))} className="mt-1 w-full rounded-md border border-stone-100/10 bg-zinc-950 p-2 text-stone-100"><option value="">Choose {group.label}</option>{group.options.map((option) => <option key={option.key} value={option.key}>{option.label}{option.price_adjustment_minor ? ` (+${dollars(option.price_adjustment_minor)})` : ""}</option>)}</select></label>)}{(selectedItem.product.customization.text_fields || []).map((field) => <label key={field.key} className="block text-sm text-stone-200">{field.label}{field.required ? " *" : ""}<input value={customizationDraft.personalization[field.key] || ""} maxLength={field.maximum_length} placeholder={field.placeholder} onChange={(event) => setCustomizationDraft((current) => ({ ...current, personalization: { ...current.personalization, [field.key]: event.target.value } }))} className="mt-1 w-full rounded-md border border-stone-100/10 bg-zinc-950 p-2 text-stone-100" /></label>)}{selectedItem.product.customization.allow_additional_notes && <label className="block text-sm text-stone-200">Additional notes<textarea maxLength={selectedItem.product.customization.additional_notes_max_length} value={customizationDraft.additional_notes} onChange={(event) => setCustomizationDraft((current) => ({ ...current, additional_notes: event.target.value }))} className="mt-1 min-h-20 w-full rounded-md border border-stone-100/10 bg-zinc-950 p-2 text-stone-100" /></label>}<Button type="button" onClick={addCustomizedItem} className="w-full bg-[#26C6D5] text-[#15191C]">Customize & Add to Cart</Button></div>}
                {customizationEstimate != null && <LaserEtchedPrice amountMinor={customizationEstimate} replayKey={`${selectedItem.id}-${JSON.stringify(customizationDraft)}`} className="mt-5 rounded-md border border-[#D8A34B]/50 bg-zinc-950/90 px-3 py-2 text-xl font-semibold text-[#D8A34B]" />}
                <div className="mt-8 space-y-3">
                  <Link to="/quote" state={{ inspiration: selectedItem.title, category: Array.isArray(selectedItem.categories) ? selectedItem.categories[0] : undefined }} onClick={() => setSelectedItem(null)}>
                    <Button className="w-full bg-[#26C6D5] hover:bg-[#1ab7d2] text-[#15191C] rounded-md h-11">
                      Request Something Similar <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/quote" state={{ inspiration: selectedItem.title, category: Array.isArray(selectedItem.categories) ? selectedItem.categories[0] : undefined }} onClick={() => setSelectedItem(null)}>
                    <Button variant="outline" className="w-full bg-transparent border-[#26C6D5]/40 text-[#26C6D5] hover:bg-[#26C6D5]/10 rounded-md h-11">
                      Get a Custom Quote
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
